import { createContext, useContext, MutableRefObject, useRef } from 'react';

import { assertUtil } from '@pukka/common/utils';
import once from 'lodash/once';
import { proxy } from 'valtio';

interface SelectModalItemContext<S, D, H> {
  selected: S;
  disabled: D;
  updating: boolean;
  hidden: H;
}

const createSelectModalItemContext = once(<S, D, H>() => createContext({} as SelectModalItemContext<S, D, H>));
const useSelectModalItemContext = <S, D, H>() => useContext(createSelectModalItemContext<S, D, H>());

function createSelectModalProxy<S, D, H>(initialValue: SelectModalItemContext<S, D, H>) {
  const State = proxy(initialValue);

  return State;
}

function createSelectModalState<S, D, H>(
  ref: MutableRefObject<SelectModalItemContext<S, D, H> | null>,
  initialValue: SelectModalItemContext<S, D, H>,
) {
  if (ref.current === null) {
    ref.current = createSelectModalProxy(initialValue);
  }

  return ref.current;
}

function useSelectModalItemState<S, D, H>(defaults: SelectModalItemContext<S, D, H>) {
  const state = useRef<SelectModalItemContext<S, D, H>>(null);

  createSelectModalState(state, defaults);
  assertUtil.assertRefIsNotNull(state);

  return state.current;
}

export type { SelectModalItemContext };

export {
  useSelectModalItemState,
  useSelectModalItemContext,
  createSelectModalItemContext,
  createSelectModalState as _createSelectModalState,
  createSelectModalProxy as _createSelectModalProxy,
};
